import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, Fragment as _Fragment, normalizeClass as _normalizeClass, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, renderList as _renderList, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "mall" }
const _hoisted_2 = {
  key: 0,
  class: "mall__ops",
  role: "status"
}
const _hoisted_3 = { class: "mall__ops-inner" }
const _hoisted_4 = {
  key: 1,
  class: "mall__deal"
}
const _hoisted_5 = { class: "mall__deal-inner" }
const _hoisted_6 = { class: "mall__deal-copy" }
const _hoisted_7 = { class: "mall__deal-eyebrow" }
const _hoisted_8 = { class: "mall__deal-title" }
const _hoisted_9 = {
  key: 0,
  class: "mall__deal-desc"
}
const _hoisted_10 = { class: "mall__deal-price" }
const _hoisted_11 = { class: "mall__deal-visual" }
const _hoisted_12 = ["src", "alt"]
const _hoisted_13 = {
  key: 2,
  class: "mall__promo",
  "aria-label": "이벤트 안내"
}
const _hoisted_14 = ["innerHTML"]
const _hoisted_15 = {
  id: "mall-catalog",
  class: "mall__main"
}
const _hoisted_16 = {
  key: 0,
  class: "mall__result-head"
}
const _hoisted_17 = { class: "mall__result-title" }
const _hoisted_18 = {
  key: 1,
  class: "mall__section-head mall__catalog-head"
}
const _hoisted_19 = { class: "mall__toolbar" }
const _hoisted_20 = { class: "mall__count" }
const _hoisted_21 = { key: 0 }
const _hoisted_22 = { class: "mall__toolbar-right" }
const _hoisted_23 = {
  class: "mall__density",
  role: "group",
  "aria-label": "목록 보기"
}
const _hoisted_24 = { class: "mall__stock" }
const _hoisted_25 = { class: "mall__stock" }
const _hoisted_26 = {
  class: "mall__sort-tabs",
  role: "tablist",
  "aria-label": "정렬"
}
const _hoisted_27 = ["aria-selected", "onClick"]
const _hoisted_28 = {
  key: 'mall-skel',
  class: "mall__grid mall__grid--skel",
  "aria-busy": "true",
  "aria-label": "상품 불러오는 중"
}
const _hoisted_29 = {
  key: 'mall-empty-search',
  class: "mall__empty-search"
}
const _hoisted_30 = { class: "mall__empty" }
const _hoisted_31 = {
  key: 0,
  class: "mall__empty-block"
}
const _hoisted_32 = { class: "mall__empty-chips" }
const _hoisted_33 = ["onClick"]
const _hoisted_34 = {
  key: 1,
  class: "mall__empty-block"
}
const _hoisted_35 = { class: "mall__empty-grid" }
const _hoisted_36 = {
  key: 'mall-empty-wish',
  class: "mall__empty"
}
const _hoisted_37 = ["src"]
const _hoisted_38 = {
  key: 'mall-empty',
  class: "mall__empty"
}
const _hoisted_39 = ["src"]
const _hoisted_40 = {
  key: 7,
  class: "mall__pager",
  "aria-label": "상품 목록 페이지"
}
const _hoisted_41 = ["disabled"]
const _hoisted_42 = ["onClick"]
const _hoisted_43 = ["disabled"]

import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ProductCard from '@/components/mall/ProductCard.vue'
import AppIcon from '@/components/common/AppIcon.vue'
import { fetchMallCatalog, fetchMallHomeSections } from '@/api/mall'
import { fetchPaymentInfo } from '@/api/mallOrder'
import { useContentSnippets } from '@/composables/useContentSnippets'
import { useRecentSearches } from '@/composables/useRecentSearches'
import { useWishlist } from '@/composables/useWishlist'
import { illust } from '@/constants/illust'
import { MALL_HOME_PATH, mallProductPath } from '@/constants/mall'
import {
  MALL_CATEGORIES,
  MALL_DEFAULT_SORT,
  MALL_SORT_OPTIONS,
  formatPrice,
  getBestProducts,
  type MallCategory,
  type MallProduct,
  type MallSortKey,
} from '@/constants/mallProducts'
import { storefrontOffer } from '@/utils/mallSell'

const OPS_DISMISS_KEY = 'mall.opsNotice.dismissed'
const DENSITY_KEY = 'portal.mall.listDensity'
const PAGE_SIZE = 20

type Density = 'comfort' | 'compact'


export default /*@__PURE__*/_defineComponent({
  __name: 'MallPage',
  setup(__props) {

const route = useRoute()
const router = useRouter()
const { wishlist } = useWishlist()
const { recentSearches } = useRecentSearches()
const { loadSnippets, snippet } = useContentSnippets()

const sortOptions = MALL_SORT_OPTIONS

const sortKey = computed<MallSortKey>({
  get: () => {
    const value = typeof route.query.sort === 'string' ? route.query.sort : MALL_DEFAULT_SORT
    return MALL_SORT_OPTIONS.some((option) => option.key === value)
      ? (value as MallSortKey)
      : MALL_DEFAULT_SORT
  },
  set: (value) => {
    const query = { ...route.query }
    if (value === MALL_DEFAULT_SORT) delete query.sort
    else query.sort = value
    delete query.page
    router.replace({ path: route.path, query })
  },
})

const page = computed({
  get: () => {
    const raw = Number(route.query.page)
    return Number.isFinite(raw) && raw >= 1 ? Math.floor(raw) : 1
  },
  set: (value: number) => {
    const query = { ...route.query }
    if (value <= 1) delete query.page
    else query.page = String(value)
    router.replace({ path: route.path, query })
    const el = document.getElementById('mall-catalog')
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  },
})

const density = ref<Density>(
  (() => {
    try {
      return localStorage.getItem(DENSITY_KEY) === 'compact' ? 'compact' : 'comfort'
    } catch {
      return 'comfort'
    }
  })(),
)

watch(density, (value) => {
  try {
    localStorage.setItem(DENSITY_KEY, value)
  } catch {
    /* ignore */
  }
})

const loading = ref(true)
const filteredProducts = ref<MallProduct[]>([])
const hero = ref<MallProduct | null>(null)
const opsNotice = ref('')
const popularFallback = ref<MallProduct[]>([])
const promoHtml = computed(() => snippet('mall.home.promo_html').trim())

const totalPages = computed(() =>
  Math.max(1, Math.ceil(filteredProducts.value.length / PAGE_SIZE)),
)

const pagedProducts = computed(() => {
  const start = (page.value - 1) * PAGE_SIZE
  return filteredProducts.value.slice(start, start + PAGE_SIZE)
})

const pageNumbers = computed(() => {
  const total = totalPages.value
  const current = page.value
  const windowSize = 5
  let start = Math.max(1, current - Math.floor(windowSize / 2))
  let end = Math.min(total, start + windowSize - 1)
  start = Math.max(1, end - windowSize + 1)
  return Array.from({ length: end - start + 1 }, (_, i) => start + i)
})

const heroOffer = computed(() => storefrontOffer(hero.value || { price: 0 }))

function dismissOpsNotice() {
  const text = opsNotice.value
  opsNotice.value = ''
  if (!text) return
  try {
    sessionStorage.setItem(OPS_DISMISS_KEY, text)
  } catch {
    /* ignore */
  }
}

async function loadOpsNotice() {
  try {
    const payment = await fetchPaymentInfo()
    const text = String(payment.opsNotice || '').trim()
    if (!text) {
      opsNotice.value = ''
      return
    }
    const dismissed = sessionStorage.getItem(OPS_DISMISS_KEY)
    opsNotice.value = dismissed === text ? '' : text
  } catch {
    opsNotice.value = ''
  }
}

function goSearch(q: string) {
  router.push({ path: MALL_HOME_PATH, query: { q } })
}

const keyword = computed(() => (typeof route.query.q === 'string' ? route.query.q.trim() : ''))

const category = computed<'전체' | MallCategory>(() => {
  const value = typeof route.query.cat === 'string' ? route.query.cat : '전체'
  return (MALL_CATEGORIES as string[]).includes(value) ? (value as '전체' | MallCategory) : '전체'
})

const inStockOnly = computed({
  get: () => route.query.stock === '1',
  set: (on: boolean) => {
    const query = { ...route.query }
    if (on) query.stock = '1'
    else delete query.stock
    delete query.page
    router.replace({ path: route.path, query })
  },
})

const wishOnly = computed({
  get: () => route.query.wish === '1',
  set: (on: boolean) => {
    const query = { ...route.query }
    if (on) query.wish = '1'
    else delete query.wish
    delete query.page
    router.replace({ path: route.path, query })
  },
})

/** 검색·카테고리일 때 결과 헤더. 관심/재고는 목록만 좁힌다. */
const isBrowsingFiltered = computed(() => Boolean(keyword.value) || category.value !== '전체')
const showHero = computed(() => !keyword.value && category.value === '전체')

const loadCatalog = async () => {
  loading.value = true
  try {
    let products = await fetchMallCatalog({
      category: category.value,
      keyword: keyword.value,
      sort: sortKey.value,
      inStockOnly: inStockOnly.value,
    })
    if (wishOnly.value) {
      const wished = new Set(wishlist.value)
      products = products.filter((product) => wished.has(product.id))
    }
    filteredProducts.value = products
    if (page.value > Math.max(1, Math.ceil(products.length / PAGE_SIZE))) {
      page.value = 1
    }
  } finally {
    loading.value = false
  }
}

const loadHome = async () => {
  const sections = await fetchMallHomeSections()
  hero.value = sections.hero
}

watch([keyword, category, inStockOnly, wishOnly, sortKey, wishlist], loadCatalog)

onMounted(async () => {
  await Promise.all([
    loadHome(),
    loadCatalog(),
    loadOpsNotice(),
    loadSnippets(['mall.home.promo_html']),
  ])
  popularFallback.value = getBestProducts(4)
})

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (opsNotice.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(AppIcon, {
              icon: "mdi-bullhorn-outline",
              size: 18
            }),
            _createElementVNode("p", null, _toDisplayString(opsNotice.value), 1),
            _createElementVNode("button", {
              type: "button",
              class: "mall__ops-close",
              "aria-label": "공지 닫기",
              onClick: dismissOpsNotice
            }, [
              _createVNode(AppIcon, {
                icon: "mdi-close",
                size: 16
              })
            ])
          ])
        ]))
      : _createCommentVNode("", true),
    (showHero.value && hero.value)
      ? (_openBlock(), _createElementBlock("section", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createVNode(_component_router_link, {
              to: _unref(mallProductPath)(hero.value.id),
              class: "mall__deal-banner"
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_6, [
                  _createElementVNode("span", _hoisted_7, [
                    _createVNode(AppIcon, {
                      icon: "mdi-lightning-bolt",
                      size: 16
                    }),
                    _cache[6] || (_cache[6] = _createTextVNode(" 이달의 특가 ", -1))
                  ]),
                  _createElementVNode("h1", _hoisted_8, _toDisplayString(hero.value.name), 1),
                  (hero.value.summary)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_9, _toDisplayString(hero.value.summary), 1))
                    : _createCommentVNode("", true),
                  _createElementVNode("div", _hoisted_10, [
                    _createElementVNode("strong", null, _toDisplayString(_unref(formatPrice)(heroOffer.value.price)) + "원", 1)
                  ]),
                  _cache[7] || (_cache[7] = _createElementVNode("span", { class: "mall__deal-cta" }, "상품 보러가기", -1))
                ]),
                _createElementVNode("div", _hoisted_11, [
                  _createElementVNode("img", {
                    src: hero.value.image,
                    alt: hero.value.name
                  }, null, 8, _hoisted_12)
                ])
              ]),
              _: 1
            }, 8, ["to"])
          ])
        ]))
      : _createCommentVNode("", true),
    (showHero.value && promoHtml.value)
      ? (_openBlock(), _createElementBlock("section", _hoisted_13, [
          _createElementVNode("div", {
            class: "mall__promo-inner",
            innerHTML: promoHtml.value
          }, null, 8, _hoisted_14)
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("main", _hoisted_15, [
      (isBrowsingFiltered.value)
        ? (_openBlock(), _createElementBlock("header", _hoisted_16, [
            _createElementVNode("h1", _hoisted_17, [
              (keyword.value)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _cache[8] || (_cache[8] = _createTextVNode("'", -1)),
                    _createElementVNode("em", null, _toDisplayString(keyword.value), 1),
                    _cache[9] || (_cache[9] = _createTextVNode("' 검색결과", -1))
                  ], 64))
                : (category.value !== '전체')
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                      _createTextVNode(_toDisplayString(category.value), 1)
                    ], 64))
                  : _createCommentVNode("", true)
            ])
          ]))
        : (showHero.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_18, [...(_cache[10] || (_cache[10] = [
              _createElementVNode("h2", null, "전체상품", -1)
            ]))]))
          : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_19, [
        _createElementVNode("span", _hoisted_20, [
          (category.value !== '전체')
            ? (_openBlock(), _createElementBlock("strong", _hoisted_21, _toDisplayString(category.value), 1))
            : _createCommentVNode("", true),
          _createTextVNode(" 총 " + _toDisplayString(filteredProducts.value.length) + "개 ", 1)
        ]),
        _createElementVNode("div", _hoisted_22, [
          _createElementVNode("div", _hoisted_23, [
            _createElementVNode("button", {
              type: "button",
              class: _normalizeClass(["mall__density-btn", { 'is-active': density.value === 'comfort' }]),
              "aria-label": "기본",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (density.value = 'comfort'))
            }, [
              _createVNode(AppIcon, {
                icon: "mdi-view-grid-outline",
                size: 18
              })
            ], 2),
            _createElementVNode("button", {
              type: "button",
              class: _normalizeClass(["mall__density-btn", { 'is-active': density.value === 'compact' }]),
              "aria-label": "작게",
              onClick: _cache[1] || (_cache[1] = ($event: any) => (density.value = 'compact'))
            }, [
              _createVNode(AppIcon, {
                icon: "mdi-view-comfy-outline",
                size: 18
              })
            ], 2)
          ]),
          _createElementVNode("label", _hoisted_24, [
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((wishOnly).value = $event)),
              type: "checkbox"
            }, null, 512), [
              [_vModelCheckbox, wishOnly.value]
            ]),
            _cache[11] || (_cache[11] = _createTextVNode(" 찜한 상품 ", -1))
          ]),
          _createElementVNode("label", _hoisted_25, [
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((inStockOnly).value = $event)),
              type: "checkbox"
            }, null, 512), [
              [_vModelCheckbox, inStockOnly.value]
            ]),
            _cache[12] || (_cache[12] = _createTextVNode(" 재고있음 ", -1))
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_26, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(sortOptions), (option) => {
          return (_openBlock(), _createElementBlock("button", {
            key: option.key,
            type: "button",
            role: "tab",
            class: _normalizeClass(["mall__sort-tab", { 'is-active': sortKey.value === option.key }]),
            "aria-selected": sortKey.value === option.key,
            onClick: ($event: any) => (sortKey.value = option.key)
          }, _toDisplayString(option.label), 11, _hoisted_27))
        }), 128))
      ]),
      (loading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_28, [
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(8, (n) => {
              return _createElementVNode("span", {
                key: n,
                class: "mall__skel-card"
              })
            }), 64))
          ]))
        : (pagedProducts.value.length)
          ? (_openBlock(), _createElementBlock("div", {
              key: 'mall-grid',
              class: _normalizeClass(["mall__grid", { 'mall__grid--compact': density.value === 'compact' }])
            }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pagedProducts.value, (product) => {
                return (_openBlock(), _createBlock(ProductCard, {
                  key: product.id,
                  product: product
                }, null, 8, ["product"]))
              }), 128))
            ], 2))
          : (keyword.value && !loading.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_29, [
                _createElementVNode("p", _hoisted_30, [
                  _cache[13] || (_cache[13] = _createTextVNode(" '", -1)),
                  _createElementVNode("em", null, _toDisplayString(keyword.value), 1),
                  _cache[14] || (_cache[14] = _createTextVNode("' 검색 결과가 없습니다. ", -1))
                ]),
                (_unref(recentSearches).length)
                  ? (_openBlock(), _createElementBlock("section", _hoisted_31, [
                      _cache[15] || (_cache[15] = _createElementVNode("h2", null, "최근 검색어", -1)),
                      _createElementVNode("div", _hoisted_32, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(recentSearches), (item) => {
                          return (_openBlock(), _createElementBlock("button", {
                            key: item,
                            type: "button",
                            class: "mall__empty-chip",
                            onClick: ($event: any) => (goSearch(item))
                          }, _toDisplayString(item), 9, _hoisted_33))
                        }), 128))
                      ])
                    ]))
                  : _createCommentVNode("", true),
                (popularFallback.value.length)
                  ? (_openBlock(), _createElementBlock("section", _hoisted_34, [
                      _cache[16] || (_cache[16] = _createElementVNode("h2", null, "인기상품", -1)),
                      _createElementVNode("div", _hoisted_35, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(popularFallback.value, (product) => {
                          return (_openBlock(), _createBlock(ProductCard, {
                            key: `pop-${product.id}`,
                            product: product
                          }, null, 8, ["product"]))
                        }), 128))
                      ])
                    ]))
                  : _createCommentVNode("", true)
              ]))
            : (!loading.value && wishOnly.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_36, [
                  _createElementVNode("img", {
                    class: "mall__empty-art",
                    src: _unref(illust).empty,
                    alt: ""
                  }, null, 8, _hoisted_37),
                  _cache[17] || (_cache[17] = _createElementVNode("p", null, "찜한 상품이 없습니다.", -1))
                ]))
              : (!loading.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_38, [
                    _createElementVNode("img", {
                      class: "mall__empty-art",
                      src: _unref(illust).empty,
                      alt: ""
                    }, null, 8, _hoisted_39),
                    _cache[18] || (_cache[18] = _createElementVNode("p", null, "조건에 맞는 상품이 없습니다.", -1))
                  ]))
                : _createCommentVNode("", true),
      (!loading.value && totalPages.value > 1)
        ? (_openBlock(), _createElementBlock("nav", _hoisted_40, [
            _createElementVNode("button", {
              type: "button",
              class: "mall__pager-btn",
              disabled: page.value <= 1,
              onClick: _cache[4] || (_cache[4] = ($event: any) => (page.value = page.value - 1))
            }, " 이전 ", 8, _hoisted_41),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pageNumbers.value, (n) => {
              return (_openBlock(), _createElementBlock("button", {
                key: n,
                type: "button",
                class: _normalizeClass(["mall__pager-btn", { 'is-active': n === page.value }]),
                onClick: ($event: any) => (page.value = n)
              }, _toDisplayString(n), 11, _hoisted_42))
            }), 128)),
            _createElementVNode("button", {
              type: "button",
              class: "mall__pager-btn",
              disabled: page.value >= totalPages.value,
              onClick: _cache[5] || (_cache[5] = ($event: any) => (page.value = page.value + 1))
            }, " 다음 ", 8, _hoisted_43)
          ]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})